import global from "../../../common/utility/global";
import { NOMBRES_MES_CORTO } from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda/tooltip mantengan siempre el
// mismo ancho/tamano; sin esto, un cliente con nombre muy largo (razon
// social) estira la leyenda y deforma el layout del grafico.
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "2026-08-27" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

// Arma la serie de "Evolucion" (top 10 clientes) a partir del detalle diario
// que devuelve el servicio: una fila por cliente y fecha, ya separada en
// soles y dolares (totalSoles/totalDolares). Se agrupa por dia si el filtro
// cae en un mismo mes, o por mes si abarca mas de uno (mismo criterio que
// construirEvolucionDesdeDetalle de DashboardEstablecimiento).
function construirEvolucionDesdeDetalle(detalle, filter, clientesReferencia) {
    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;
    const nombresTop = clientesReferencia.slice(0, 10).map((item) => item.nombre);

    const detalleOrdenado = detalle
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha);

    const ordenClaves = [];
    const vistos = new Set();
    detalleOrdenado.forEach((fila) => {
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        if (!vistos.has(clave)) {
            vistos.add(clave);
            ordenClaves.push(clave);
        }
    });

    const acumuladoPorCliente = {};
    nombresTop.forEach((nombre) => {
        acumuladoPorCliente[nombre] = {};
    });
    detalleOrdenado.forEach((fila) => {
        if (!nombresTop.includes(fila.nombre)) return;
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        const previo = acumuladoPorCliente[fila.nombre][clave] || { pen: 0, usd: 0 };
        previo.pen += Number(fila.totalSoles) || 0;
        previo.usd += Number(fila.totalDolares) || 0;
        acumuladoPorCliente[fila.nombre][clave] = previo;
    });

    const construirSerie = (moneda) => nombresTop.map((nombre) => ({
        name: nombre,
        data: ordenClaves.map((clave) => Math.round((acumuladoPorCliente[nombre][clave] || { pen: 0, usd: 0 })[moneda])),
    }));

    return {
        timeLine: ordenClaves,
        pen: construirSerie("pen"),
        usd: construirSerie("usd"),
    };
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

function construirKpiMoneda(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

// Concentracion Top 10 se expresa en puntos porcentuales (p.p.), no en %
// relativo: la variacion es la resta directa entre el % actual y el % del
// periodo anterior, cada uno ya calculado sobre su propio total.
function construirConcentracionTop10(ventasTop10, ventasTop10Anterior, totalVentas, totalVentasAnterior) {
    const pctActual = totalVentas > 0 ? (Number(ventasTop10) / totalVentas) * 100 : 0;
    const pctAnterior = totalVentasAnterior > 0 ? (Number(ventasTop10Anterior) / totalVentasAnterior) * 100 : 0;
    return {
        valor: pctActual,
        tendenciaPct: pctActual - pctAnterior,
        tendenciaDireccion: direccionTendencia(pctActual, pctAnterior),
    };
}

// Arma el grafico de "Ventas por Rango de Clientes" a partir de los rangos
// ya agregados que manda el backend (Top 10, Del 11 al 50, Del 51 al 100,
// Mas de 100), cada uno con su total en soles y en dolares. El "porcentaje"
// que trae la API es fijo (calculado en soles); aqui se recalcula por
// moneda para que la dona cuadre al 100% tambien al cambiar a dolares.
// Se omiten los rangos vacios (por ejemplo, si el periodo tiene menos de
// 10 clientes).
function construirRangoClientes(rangosApi, moneda) {
    const campoTotal = moneda === "usd" ? "totalVentaDolares" : "totalVentaSoles";
    const totalGeneral = rangosApi.reduce((acumulado, item) => acumulado + (Number(item[campoTotal]) || 0), 0);
    return rangosApi
        .map((item) => {
            const total = Number(item[campoTotal]) || 0;
            return {
                nombre: item.segmento,
                total,
                porcentaje: totalGeneral > 0 ? (total / totalGeneral) * 100 : 0,
            };
        })
        .filter((rango) => rango.total > 0);
}

export function buildDashboardData(response, filter) {
    // El servicio real (comprobanteService.dashboardCliente) devuelve:
    // - clientes: detalle diario (nombre del cliente, fecha,
    //   totalSoles, totalDolares), una fila por cliente y fecha con ventas.
    // - kpis: arreglo de un solo elemento con los totales del periodo y su
    //   comparacion contra el periodo anterior (sufijo "Anterior"):
    //   totalVentasSoles/Dolares, clientesActivos, cantidadCPE,
    //   clientesNuevos y ventasTop10Soles/Dolares (para la concentracion).
    // - rangos: totales ya agrupados por segmento de cliente (Top 10, Del 11
    //   al 50, Del 51 al 100, Mas de 100), en soles y dolares.
    const detalle = (response && response.clientes) || [];
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};
    const rangosApi = (response && response.rangos) || [];

    const acumuladoPorCliente = {};
    detalle.forEach((fila) => {
        const previo = acumuladoPorCliente[fila.nombre] || { pen: 0, usd: 0 };
        previo.pen += Number(fila.totalSoles) || 0;
        previo.usd += Number(fila.totalDolares) || 0;
        acumuladoPorCliente[fila.nombre] = previo;
    });

    const clientesOrdenados = Object.keys(acumuladoPorCliente)
        .map((nombre) => ({ nombre, pen: acumuladoPorCliente[nombre].pen, usd: acumuladoPorCliente[nombre].usd }))
        .sort((a, b) => b.pen - a.pen);

    const evolucion = construirEvolucionDesdeDetalle(detalle, filter, clientesOrdenados);

    const cantidadCPE = Number(kpisApi.cantidadCPE) || 0;
    const cantidadCPEAnterior = Number(kpisApi.cantidadCPEAnterior) || 0;

    return {
        kpis: {
            totalVentas: {
                pen: construirKpiMoneda(kpisApi.totalVentasSoles, kpisApi.totalVentasSolesAnterior),
                usd: construirKpiMoneda(kpisApi.totalVentasDolares, kpisApi.totalVentasDolaresAnterior),
            },
            clientes: {
                valor: Number(kpisApi.clientesActivos) || 0,
                tendenciaPct: calcularTendenciaPct(Number(kpisApi.clientesActivos) || 0, Number(kpisApi.clientesActivosAnterior) || 0),
                tendenciaDireccion: direccionTendencia(Number(kpisApi.clientesActivos) || 0, Number(kpisApi.clientesActivosAnterior) || 0),
            },
            comprobantes: {
                valor: cantidadCPE,
                tendenciaPct: calcularTendenciaPct(cantidadCPE, cantidadCPEAnterior),
                tendenciaDireccion: direccionTendencia(cantidadCPE, cantidadCPEAnterior),
            },
            clientesNuevos: {
                valor: Number(kpisApi.clientesNuevos) || 0,
                tendenciaPct: calcularTendenciaPct(Number(kpisApi.clientesNuevos) || 0, Number(kpisApi.clientesNuevosAnterior) || 0),
                tendenciaDireccion: direccionTendencia(Number(kpisApi.clientesNuevos) || 0, Number(kpisApi.clientesNuevosAnterior) || 0),
            },
            concentracionTop10: {
                pen: construirConcentracionTop10(
                    kpisApi.ventasTop10Soles, kpisApi.ventasTop10SolesAnterior,
                    Number(kpisApi.totalVentasSoles) || 0, Number(kpisApi.totalVentasSolesAnterior) || 0
                ),
                usd: construirConcentracionTop10(
                    kpisApi.ventasTop10Dolares, kpisApi.ventasTop10DolaresAnterior,
                    Number(kpisApi.totalVentasDolares) || 0, Number(kpisApi.totalVentasDolaresAnterior) || 0
                ),
            },
        },
        topClientes: clientesOrdenados.slice(0, 10),
        rangoClientes: {
            pen: construirRangoClientes(rangosApi, "pen"),
            usd: construirRangoClientes(rangosApi, "usd"),
        },
        evolucion,
    };
}
